import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';
import { useAuth } from '@/hooks/use-auth';
import { useMentorshipEnrollment } from '@/hooks/useMentorshipEnrollment';
import { useSubscription } from '@/hooks/useSubscription';
import { useFoundingMembership } from '@/hooks/useFoundingMembership';
import { useDataPlan } from '@/hooks/useDataPlan';
import { useClubMember } from '@/hooks/useClubMember';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChangePasswordModal } from '@/components/dashboard/ChangePasswordModal';
import { DataTab } from '@/components/dashboard/DataTab';
import { ClubTab } from '@/components/dashboard/ClubTab';
import { HelpTab } from '@/components/dashboard/HelpTab';
import { AccountTab } from '@/components/dashboard/AccountTab';
import {
  LogOut, User, Loader2, ArrowLeft,
  Database, Users, HelpCircle, Shield
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { LogoMark, Wordmark } from '@/components/brand/Logo';

export default function Dashboard() {
  const { user, isLoading: authLoading, signOut, isAdmin } = useAuth();
  const { enrollment, isEnrolled, isLoading: enrollmentLoading } = useMentorshipEnrollment(user);
  const { subscription, isSubscribed, isLoading: subLoading } = useSubscription(user);
  const { membership, isFoundingMember, hasFoundingBadge, isLoading: foundingLoading } = useFoundingMembership(user);
  const { current: dataPlan, loading: dataLoading } = useDataPlan(user);
  const { isClubMember, isLoading: clubLoading } = useClubMember(user);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [profileCreatedAt, setProfileCreatedAt] = useState<string | undefined>();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const isLoading = authLoading || enrollmentLoading || subLoading || foundingLoading || dataLoading || clubLoading;

  // One tab per product (his ruling, 30 Oct 2026): Data for everyone, Club for Club members only.
  // ?tab= opens a tab; otherwise paying and Founding Club members with no Data plan land on Club.
  const asked = params.get('tab');
  const tabs = isClubMember ? ['data', 'club', 'account', 'help'] : ['data', 'account', 'help'];
  const tab = asked && tabs.includes(asked)
    ? asked
    : isClubMember && (isSubscribed || isFoundingMember) && !dataPlan ? 'club' : 'data';
  const pickTab = (t: string) => setParams((p) => { p.set('tab', t); return p; }, { replace: true });

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
    }
  }, [user, authLoading, navigate]);

  // Fetch profile created_at
  useEffect(() => {
    if (user) {
      supabase
        .from('profiles')
        .select('created_at')
        .eq('user_id', user.id)
        .maybeSingle()
        .then(({ data }) => {
          if (data) setProfileCreatedAt(data.created_at);
        });
    }
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-accent animate-spin" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background relative">
      {/* Background effects */}
      <div className="hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-accent/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
      </div>


      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-[var(--sn-border)]">
          <div className="mx-auto w-full max-w-[1120px] px-5 py-4 sm:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <LogoMark size={40} />
                <div>
                  <Wordmark size={18} />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Back to Site</span>
                </Link>
                {isAdmin && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/admin')}
                    className="text-accent border-accent/30 hover:bg-accent/10"
                  >
                    Admin
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSignOut}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Logout</span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="mx-auto w-full max-w-[1120px] px-5 py-10 sm:px-8">
          {/* Welcome */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="mb-8"
          >
            <Card className="rounded-none border-0 border-b border-[var(--sn-border)] bg-transparent px-0 pb-7 shadow-none">
              <div className="flex items-start gap-4">
                <div className="hidden">
                  <User className="w-7 h-7 text-accent" />
                </div>
                <div>
                  <h2 className="text-[clamp(28px,3.4vw,40px)] font-semibold leading-[1.08] tracking-[-0.03em] text-foreground">Your account</h2>
                  <p className="mt-2 font-mono text-[12.5px] text-muted-foreground">{user.email}</p>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* Tabs */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25, delay: 0.1 }}
          >
            <Tabs value={tab} onValueChange={pickTab} className="space-y-6">
              <TabsList className="h-auto w-full justify-start gap-6 overflow-x-auto rounded-none border-b border-[var(--sn-border)] bg-transparent p-0 sm:w-auto">
                <TabsTrigger value="data" className="gap-2 rounded-none border-b-2 border-transparent px-0 py-2.5 font-mono text-[11px] uppercase tracking-[0.12em] data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                  <Database className="w-4 h-4" /> Data
                </TabsTrigger>
                {isClubMember && (
                  <TabsTrigger value="club" className="gap-2 rounded-none border-b-2 border-transparent px-0 py-2.5 font-mono text-[11px] uppercase tracking-[0.12em] data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                    <Users className="w-4 h-4" /> Club
                  </TabsTrigger>
                )}
                <TabsTrigger value="account" className="gap-2 rounded-none border-b-2 border-transparent px-0 py-2.5 font-mono text-[11px] uppercase tracking-[0.12em] data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                  <Shield className="w-4 h-4" /> Account
                </TabsTrigger>
                <TabsTrigger value="help" className="gap-2 rounded-none border-b-2 border-transparent px-0 py-2.5 font-mono text-[11px] uppercase tracking-[0.12em] data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none">
                  <HelpCircle className="w-4 h-4" /> Help
                </TabsTrigger>
              </TabsList>

              <TabsContent value="data">
                <DataTab user={user} />
              </TabsContent>

              {isClubMember && (
                <TabsContent value="club">
                  <ClubTab
                    user={user}
                    subscription={subscription}
                    isSubscribed={isSubscribed}
                    membership={membership}
                    isFoundingMember={isFoundingMember}
                    hasFoundingBadge={hasFoundingBadge}
                  />
                </TabsContent>
              )}

              <TabsContent value="account">
                <AccountTab
                  user={user}
                  profileCreatedAt={profileCreatedAt}
                  onSignOut={handleSignOut}
                  onChangePassword={() => setShowPasswordModal(true)}
                  isClubMember={isClubMember}
                  enrollment={enrollment}
                  isEnrolled={isEnrolled}
                />
              </TabsContent>

              <TabsContent value="help">
                <HelpTab user={user} />
              </TabsContent>
            </Tabs>
          </motion.div>

        </main>
      </div>

      <ChangePasswordModal
        isOpen={showPasswordModal}
        onClose={() => setShowPasswordModal(false)}
      />
    </div>
  );
}
